import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { InventoryTransferTableDTO } from "../../../types/inventory/transfer/InventoryTransferTypes";
import { InventoryTransferDetailDTO } from "../../../types/inventory/transfer/InventoryTransferDetailTypes";

/**
 * Estado global para los TRASLADOS de inventario (consulta).
 */
interface InventoryTransferQueryState {
    list: InventoryTransferTableDTO[];        // Página principal de traslados
    paginator: any;                           // Paginación
    pendingCount: number | null;              // Conteo rápido
    pendingTransfers: InventoryTransferTableDTO[];
    searchResults: InventoryTransferTableDTO[];
    selectedTransfer: InventoryTransferTableDTO | null;
    detailTransfer: InventoryTransferDetailDTO | null;
    isLoading: boolean;
    error: string | null;
    showPendingTransferModal: boolean;
    showDetailTransferModal: boolean;
}

/**
 * Estado inicial.
 */
const initialState: InventoryTransferQueryState = {
    list: [],
    paginator: null,
    pendingCount: null,

    pendingTransfers: [],
    searchResults: [],

    selectedTransfer: null,
    detailTransfer: null,
    isLoading: false,
    error: null,
    showPendingTransferModal: false,
    showDetailTransferModal: false,
};

const inventoryTransferQuerySlice = createSlice({
    name: "inventoryTransferQuery",
    initialState,
    reducers: {
        // Tabla paginada
        setTransfersList(
            state,
            action: PayloadAction<{
                content: InventoryTransferTableDTO[];
                paginator: any;
            }>
        ) {
            state.list = action.payload.content;
            state.paginator = action.payload.paginator;
        },

        clearTransfersList(state) {
            state.list = [];
            state.paginator = null;
        },

        // Conteo de pendientes
        setPendingCount(state, action: PayloadAction<number>) {
            state.pendingCount = action.payload;
        },

        // ➕ Guardar la lista simple de pendientes
        setPendingTransfers(
            state,
            action: PayloadAction<InventoryTransferTableDTO[]>
        ) {
            state.pendingTransfers = action.payload;
        },

        addPendingTransfer(
            state,
            action: PayloadAction<InventoryTransferTableDTO>
        ) {
            const created = action.payload;

            // Agregar al inicio de la lista de pendientes
            state.pendingTransfers = [created, ...state.pendingTransfers];

            // Si en la tabla principal se muestran pendientes, también agregar
            state.list = [created, ...state.list];
        },

        updatePendingTransfer(
            state,
            action: PayloadAction<InventoryTransferTableDTO>
        ) {
            const updated = action.payload;

            state.pendingTransfers = state.pendingTransfers.map(t =>
                t.id === updated.id ? updated : t
            );

            // Si la tabla LIST principal muestra pendientes, también actualizarla
            state.list = state.list.map(t =>
                t.id === updated.id ? updated : t
            );
        },

        removePendingTransfer(
            state,
            action: PayloadAction<number> // id del traslado
        ) {
            const id = action.payload;

            state.pendingTransfers = state.pendingTransfers.filter(t => t.id !== id);
        },

        // ➕ Guardar resultados de búsqueda
        setSearchResults(
            state,
            action: PayloadAction<InventoryTransferTableDTO[]>
        ) {
            state.searchResults = action.payload;
        },

        // Seleccionar traslado
        setSelectedTransfer(
            state,
            action: PayloadAction<InventoryTransferTableDTO | null>
        ) {
            state.selectedTransfer = action.payload;
        },

        setDetailTransfer(state, action: PayloadAction<InventoryTransferDetailDTO | null>) {
            state.detailTransfer = action.payload;
        },

        // Loading
        setIsLoading(state, action: PayloadAction<boolean>) {
            state.isLoading = action.payload;
        },

        // Error
        setError(state, action: PayloadAction<string | null>) {
            state.error = action.payload;
        },
        openPendingTransferModal(state) {
            state.showPendingTransferModal = true;
        },

        closePendingTransferModal(state) {
            state.showPendingTransferModal = false;
        },

        // 🆕 Abrir modal de detalle
        openDetailTransferModal(state) {
            state.showDetailTransferModal = true;
        },

        // 🆕 Cerrar modal de detalle
        closeDetailTransferModal(state) {
            state.showDetailTransferModal = false;
            state.detailTransfer = null;
        }
    }
});

// Exportar acciones
export const {
    setTransfersList,
    clearTransfersList,
    setPendingCount,
    setPendingTransfers,
    addPendingTransfer,
    updatePendingTransfer,
    removePendingTransfer,
    setSearchResults,
    setSelectedTransfer,
    setDetailTransfer,
    setIsLoading,
    setError,
    openPendingTransferModal,
    closePendingTransferModal,
    openDetailTransferModal,
    closeDetailTransferModal
} = inventoryTransferQuerySlice.actions;

// Reducer
export default inventoryTransferQuerySlice.reducer;
